import { useMemo, useState, useCallback } from "react";
import DashboardPanelSwitcher from "../Common/DashboardPanelSwitcher";
import FilterBar from "../PharmaDashboard/components/FilterBar/FilterBar";
import { useFilters } from "../PharmaDashboard/context/FilterContext";
import { generateExecutiveFocusNote } from "../../services/executiveFocusNoteApi";
import {
  formatMetricLabel,
  formatMetricValue,
  sanitizeExecutiveText,
} from "../../services/executiveFocusNoteAnalytics";
import { getTopBrandsForDivision } from "../../config/divisionTopBrands";
import "./ExecutiveFocusNotePanel.css";

const SECTION_TABLES = [
  ["critical_business_alerts", "Critical Alert Tables"],
  ["growth_opportunities", "Growth Opportunities"],
  ["coverage_compliance_gaps", "Coverage & Compliance Gaps"],
  ["stockist_anomalies", "Stockist Anomalies"],
];

function getErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return sanitizeExecutiveText(detail.map((item) => item.msg).join(", "));
  if (typeof detail === "string") return sanitizeExecutiveText(detail);
  return sanitizeExecutiveText(error?.message || "Failed to generate Executive Focus Note.");
}

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const text = typeof value === "object" ? JSON.stringify(value) : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function exportCsv(filename, rows) {
  if (!rows?.length) return;
  const columns = Array.from(rows.reduce((set, row) => {
    Object.keys(row || {}).forEach((key) => set.add(key));
    return set;
  }, new Set()));
  const csv = [
    columns.map(csvEscape).join(","),
    ...rows.map((row) => columns.map((column) => csvEscape(row?.[column])).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function FilterChips({ filters }) {
  const chips = [
    ["FY", filters.fiscalYear || filters.fiscal_year],
    ["Month", filters.yearmonthLabel || filters.yearmonth],
    ["Division", filters.division],
    ["State", filters.state],
    ["BDM", filters.bdmLabel || filters.bdm],
    ["ZM", filters.zmLabel || filters.zm],
    ["HQ", filters.hq],
    ["Brand", filters.brand],
  ].filter(([, value]) => value);

  if (!chips.length) return <span className="efn-muted">No filters selected</span>;

  return (
    <div className="efn-filter-chips">
      {chips.map(([label, value]) => (
        <span key={`${label}-${value}`} className="efn-chip">
          <strong>{label}</strong>
          {value}
        </span>
      ))}
    </div>
  );
}

function ContextSummary({ context }) {
  const entries = Object.entries(context || {}).filter(([, value]) => value);
  if (!entries.length) return null;

  return (
    <div className="efn-context-summary" aria-label="Selected filter context">
      {entries.map(([label, value]) => (
        <span key={label}>
          <strong>{label}</strong>
          {value}
        </span>
      ))}
    </div>
  );
}

function CitationChip({ id, onOpen }) {
  if (!id) return null;
  return (
    <button type="button" className="efn-citation-chip" onClick={() => onOpen(id)}>
      {id}
    </button>
  );
}

function InsightCard({ insight, onOpenCitation }) {
  const citations = Array.isArray(insight?.citations) ? insight.citations : [];
  return (
    <article className="efn-insight-card">
      <div className="efn-insight-top">
        <span className={`efn-priority efn-priority--${String(insight?.priority || "low").toLowerCase()}`}>
          {insight?.priority || "Low"}
        </span>
        <strong>{insight?.focus_area || "Executive Focus"}</strong>
      </div>
      <h3>{insight?.insight || "Data not available from current query output."}</h3>
      <dl>
        {insight?.state ? (
          <div>
            <dt>State</dt>
            <dd>{insight.state}</dd>
          </div>
        ) : null}
        <div>
          <dt>Evidence</dt>
          <dd>{insight?.evidence || "-"}</dd>
        </div>
        <div>
          <dt>Reasoning Basis</dt>
          <dd>{insight?.reasoning_basis || "-"}</dd>
        </div>
        <div>
          <dt>Suggested Action</dt>
          <dd>{insight?.suggested_action || "-"}</dd>
        </div>
      </dl>
      <div className="efn-citation-row">
        {citations.map((id) => (
          <CitationChip key={id} id={id} onOpen={onOpenCitation} />
        ))}
      </div>
    </article>
  );
}

// ── Performance flag colors ──────────────────────────────────────────────────
const PERFORMANCE_FLAG_COLORS = {
  SUSPECTED_SALE_DUMP: { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
  MATERIAL_SURGE_REVERSAL: { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" },
  CONFIRMED_SLUMP: { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
  SURGE_UNDER_OBSERVATION: { bg: "#fefce8", color: "#a16207", border: "#fef08a" },
  SURGE_RETAINED: { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" },
};

function PerformanceFlagBadge({ flag }) {
  if (!flag) return null;
  const style = PERFORMANCE_FLAG_COLORS[flag] || { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0" };
  return (
    <span
      className="efn-perf-flag"
      style={{ background: style.bg, color: style.color, borderColor: style.border }}
    >
      {flag.replace(/_/g, " ")}
    </span>
  );
}

function PurchaseBehaviorTag({ behavior }) {
  if (!behavior) return null;
  const isRegular = behavior.toUpperCase() === "REGULAR";
  return (
    <span className={`efn-behavior-tag ${isRegular ? "efn-behavior-tag--regular" : "efn-behavior-tag--irregular"}`}>
      {behavior}
    </span>
  );
}

function SurgeEvidenceCard({ row }) {
  if (!row?.sales_surge_flag) return null;
  return (
    <div className="efn-surge-card">
      <div className="efn-surge-card__header">⚡ Surge Evidence</div>
      <dl className="efn-surge-card__details">
        {row.surge_month && <div><dt>Surge Month</dt><dd>{row.surge_month}</dd></div>}
        {row.surge_amount != null && <div><dt>Surge Amount</dt><dd>₹{Number(row.surge_amount).toLocaleString("en-IN")}</dd></div>}
        {row.excess_pct != null && <div><dt>Excess %</dt><dd>{Number(row.excess_pct).toFixed(1)}%</dd></div>}
        {row.post_surge_returns != null && <div><dt>Post-Surge Returns</dt><dd>₹{Number(row.post_surge_returns).toLocaleString("en-IN")}</dd></div>}
      </dl>
    </div>
  );
}

function CreditNotesBreakdown({ row }) {
  const hasCn = row?.sales_return_cn != null || row?.expiry_cn != null || row?.breakage_cn != null;
  if (!hasCn) return null;
  return (
    <div className="efn-cn-breakdown">
      {row.sales_return_cn != null && (
        <span className="efn-cn-chip efn-cn-chip--return">Sales Return: ₹{Number(row.sales_return_cn).toLocaleString("en-IN")}</span>
      )}
      {row.expiry_cn != null && (
        <span className="efn-cn-chip efn-cn-chip--expiry">Expiry: ₹{Number(row.expiry_cn).toLocaleString("en-IN")}</span>
      )}
      {row.breakage_cn != null && (
        <span className="efn-cn-chip efn-cn-chip--breakage">Breakage: ₹{Number(row.breakage_cn).toLocaleString("en-IN")}</span>
      )}
    </div>
  );
}

function SortableEvidenceTable({ title, rows, onOpenCitation }) {
  const [sort, setSort] = useState({ key: "severity_score", dir: "desc" });
  const columns = useMemo(() => {
    const preferred = [
      "severity",
      "severity_score",
      "performance_flag",
      "purchase_behavior",
      "hq_name",
      "state",
      "brand_name",
      "employee_name",
      "stockist_name",
      "actual_sales",
      "comparison_sales",
      "budget",
      "sales_gap",
      "achievement_pct",
      "growth_pct",
      "mom_growth_pct",
      "contribution_pct",
      "coverage_pct",
      "compliance_pct",
      "absolute_variance",
      "sales_return_cn",
      "expiry_cn",
      "breakage_cn",
      "sales_surge_flag",
      "surge_month",
      "surge_amount",
      "excess_pct",
      "post_surge_returns",
      "missed_doctors",
      "comparison_period",
      "business_reason",
      "citation_id",
    ];
    const found = new Set();
    rows?.forEach((row) => Object.keys(row || {}).forEach((key) => found.add(key)));
    return preferred.filter((key) => found.has(key)).slice(0, 18);
  }, [rows]);

  const sortedRows = useMemo(() => {
    const data = [...(rows || [])];
    data.sort((a, b) => {
      const av = a?.[sort.key];
      const bv = b?.[sort.key];
      const an = Number(av);
      const bn = Number(bv);
      const result = Number.isFinite(an) && Number.isFinite(bn)
        ? an - bn
        : String(av ?? "").localeCompare(String(bv ?? ""));
      return sort.dir === "asc" ? result : -result;
    });
    return data;
  }, [rows, sort]);

  if (!rows?.length) return null;

  return (
    <section className="efn-section">
      <div className="efn-section-header">
        <div>
          <h2>{title}</h2>
          <p>{rows.length} rows</p>
        </div>
        <button type="button" className="efn-secondary-btn" onClick={() => exportCsv(`${title}.csv`, rows)}>
          Export
        </button>
      </div>
      <div className="efn-critical-preview">
        {sortedRows.slice(0, 10).map((row, index) => (
          <article key={`${row.citation_id}-${index}`} className="efn-mini-card">
            <strong>{row.hq_name || row.brand_name || row.state || row.employee_name || row.stockist_name || `Row ${index + 1}`}</strong>
            <span>{(row.severity_reasons || [])[0] || row.severity || "Evidence row"}</span>
            <div className="efn-mini-card__tags">
              <PerformanceFlagBadge flag={row.performance_flag} />
              <PurchaseBehaviorTag behavior={row.purchase_behavior} />
            </div>
            <SurgeEvidenceCard row={row} />
            <CreditNotesBreakdown row={row} />
            <CitationChip id={row.citation_id} onOpen={onOpenCitation} />
          </article>
        ))}
      </div>
      <div className="efn-table-wrap">
        <table className="efn-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>
                  <button
                    type="button"
                    onClick={() => setSort((current) => ({
                      key: column,
                      dir: current.key === column && current.dir === "desc" ? "asc" : "desc",
                    }))}
                  >
                    {formatMetricLabel(column)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, index) => (
              <tr key={`${row.citation_id}-${index}`}>
                {columns.map((column) => (
                  <td key={column}>
                    {column === "citation_id" ? (
                      <CitationChip id={row[column]} onOpen={onOpenCitation} />
                    ) : column === "performance_flag" ? (
                      <PerformanceFlagBadge flag={row[column]} />
                    ) : column === "purchase_behavior" ? (
                      <PurchaseBehaviorTag behavior={row[column]} />
                    ) : formatMetricValue(row[column], column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CitationDrawer({ citation, onClose }) {
  if (!citation) return null;
  const evidenceSources = citation.evidence_sources || [];
  return (
    <aside className="efn-drawer" aria-label="Citation details">
      <div className="efn-drawer-panel">
        <div className="efn-drawer-header">
          <div>
            <h2>Citation Detail</h2>
            <p>{citation.severity} | Score {citation.severity_score}</p>
          </div>
          <button type="button" onClick={onClose}>Close</button>
        </div>
        <section>
          <h3>Evidence Sources</h3>
          {evidenceSources.length ? (
            evidenceSources.map((source, index) => (
              <span key={`${source}-${index}`} className="efn-drawer-pill">
                Evidence Source {index + 1}
              </span>
            ))
          ) : (
            <span className="efn-muted">No evidence source details available</span>
          )}
        </section>
        <section>
          <h3>Applied Filters</h3>
          <pre>{JSON.stringify(citation.filters || {}, null, 2)}</pre>
        </section>
        <section>
          <h3>Metrics</h3>
          <pre>{JSON.stringify(citation.metrics || {}, null, 2)}</pre>
        </section>
        <section>
          <h3>Severity Reasons</h3>
          <ul>
            {(citation.severity_reasons || []).map((reason) => <li key={reason}>{reason}</li>)}
          </ul>
        </section>
        <section>
          <h3>Supporting Row</h3>
          <pre>{JSON.stringify(citation.supporting_row || {}, null, 2)}</pre>
        </section>
      </div>
    </aside>
  );
}

const MAX_502_RETRIES = 2;
const RETRY_DELAY_MS = 3000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function is502Error(err) {
  return err?.response?.status === 502 || err?.status === 502;
}

function GuideModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="efn-guide-overlay" role="dialog" aria-modal="true" aria-labelledby="efn-guide-title">
      <div className="efn-guide-modal">
        <div className="efn-guide-header">
          <h2 id="efn-guide-title">How to Use Filters — Executive Focus Note</h2>
          <button type="button" className="efn-guide-close" onClick={onClose} aria-label="Close guide">
            ✕
          </button>
        </div>
        <div className="efn-guide-body">
          <section className="efn-guide-section">
            <h3>🔹 Division (Required)</h3>
            <p>Select a division to unlock all insights. Without it, you'll only see a high-level division comparison — no HQ, brand, TM, doctor, or stockist analysis.</p>
          </section>
          <section className="efn-guide-section">
            <h3>🔹 Month / Fiscal Year</h3>
            <p>Defaults to the latest available month. Change it to view historical performance. YoY and MoM comparisons are automatically calculated.</p>
          </section>
          <section className="efn-guide-section">
            <h3>🔹 State (Optional)</h3>
            <p>Narrows all data to a specific state. Useful when you want to focus on regional performance rather than pan-India.</p>
          </section>
          <section className="efn-guide-section">
            <h3>🔹 HQ / Territory (Recommended for deep insights)</h3>
            <p>Unlocks:</p>
            <ul>
              <li>TM-level coverage &amp; compliance gaps</li>
              <li>Missed doctor analysis</li>
              <li>Root cause cards</li>
              <li>6-month sales trend</li>
              <li>Lag correlation (coverage vs sales impact)</li>
            </ul>
            <p>Without HQ selected, these sections will be empty.</p>
          </section>
          <section className="efn-guide-section">
            <h3>🔹 Brand (Optional)</h3>
            <p>Filters brand-level performance and stockist data to a specific brand. Helpful for brand managers reviewing a single product's health.</p>
          </section>
          <section className="efn-guide-section">
            <h3>🔹 BDM / ZM (Optional)</h3>
            <p>Filters HQ and brand data to territories managed by a specific Business Development Manager or Zonal Manager.</p>
          </section>

          <hr className="efn-guide-divider" />

          <section className="efn-guide-section">
            <h3>💡 Recommended Combinations</h3>
            <ul>
              <li><strong>Complete executive view:</strong> Division + HQ → All sections populated</li>
              <li><strong>National overview:</strong> Division only → Division, State, HQ, Brand comparisons</li>
              <li><strong>Brand deep-dive:</strong> Division + Brand + HQ → Brand stockist &amp; doctor insights</li>
              <li><strong>TM accountability review:</strong> Division + HQ → Coverage gaps, missed doctors, root causes</li>
            </ul>
          </section>

          <hr className="efn-guide-divider" />

          <section className="efn-guide-section">
            <h3>📊 What Each Section Shows</h3>
            <div className="efn-guide-table-wrap">
              <table className="efn-guide-table">
                <thead>
                  <tr><th>Section</th><th>Requires</th></tr>
                </thead>
                <tbody>
                  <tr><td>Critical Business Alerts</td><td>Division</td></tr>
                  <tr><td>Growth Opportunities</td><td>Division</td></tr>
                  <tr><td>State Performance</td><td>Division</td></tr>
                  <tr><td>HQ Performance</td><td>Division</td></tr>
                  <tr><td>Brand Performance</td><td>Division</td></tr>
                  <tr><td>TM Coverage &amp; Compliance</td><td>Division + HQ</td></tr>
                  <tr><td>Missed Doctors</td><td>Division + HQ</td></tr>
                  <tr><td>Root Cause Indicators</td><td>Division + HQ</td></tr>
                  <tr><td>6-Month Trend</td><td>Division + HQ</td></tr>
                  <tr><td>Stockist Anomalies</td><td>Division</td></tr>
                  <tr><td>Lag Correlation Findings</td><td>Division + HQ</td></tr>
                  <tr><td>Recommended Actions</td><td>Division (richer with HQ)</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="efn-guide-section efn-guide-note">
            <p>⚠️ If a section shows "Data not available", it means the selected filter combination did not return results from the database — try broadening your filters.</p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default function ExecutiveFocusNotePanel() {
  const { filters, hasActiveFilters, resetFilters } = useFilters();
  const [data, setData] = useState(null);
  const [loadingMode, setLoadingMode] = useState("");
  const [error, setError] = useState("");
  const [activeCitationId, setActiveCitationId] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);

  const activeCitation = activeCitationId ? data?.citation_map?.[activeCitationId] : null;
  const selectedLoading = loadingMode === "selected_filters";
  const currentMonthLoading = loadingMode === "current_month";
  const isProcessing = Boolean(loadingMode);

  const run = useCallback(async (mode) => {
    if (loadingMode) return;
    setLoadingMode(mode);
    setError("");
    let lastError = null;

    // Build effective filters: auto-inject top brands if user hasn't selected any
    const effectiveFilters = mode === "current_month" && !hasActiveFilters
      ? {}
      : { ...filters };

    if (effectiveFilters.division && !effectiveFilters.brand) {
      const topBrands = getTopBrandsForDivision(effectiveFilters.division);
      if (topBrands.length > 0) {
        effectiveFilters.brand = topBrands.join(",");
      }
    }

    for (let attempt = 0; attempt <= MAX_502_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          await sleep(RETRY_DELAY_MS);
        }
        const result = await generateExecutiveFocusNote({
          mode,
          filters: effectiveFilters,
        }, { signal: undefined });
        setData(result);
        setLoadingMode("");
        return;
      } catch (err) {
        lastError = err;
        if (!is502Error(err) || attempt === MAX_502_RETRIES) {
          break;
        }
        // 502 — retry
      }
    }
    setError(getErrorMessage(lastError) || "The Executive Insight Agent could not generate the note. Please try again.");
    setData(null);
    setLoadingMode("");
  }, [loadingMode, filters, hasActiveFilters]);

  const handleCreateNew = useCallback(() => {
    setData(null);
    setError("");
    setActiveCitationId(null);
    if (resetFilters) resetFilters();
  }, [resetFilters]);

  return (
    <div className="executive-focus-note">
      <header className="efn-header">
        <div className="efn-brand">
          <h1>Emcure</h1>
          <p>Executive Focus Note</p>
        </div>
        <DashboardPanelSwitcher direction="down" />
      </header>

      <main className="efn-content">
        <FilterBar />

        <section className="efn-control-panel">
          <div>
            <h2>Executive Focus Note</h2>
            <FilterChips filters={filters} />
          </div>
          <div className="efn-actions">
            <button
              type="button"
              className="efn-guide-btn"
              onClick={() => setGuideOpen(true)}
            >
              📋 Guide
            </button>
            <button
              type="button"
              className="efn-primary-btn"
              disabled={isProcessing || !hasActiveFilters}
              aria-busy={selectedLoading}
              onClick={() => run("selected_filters")}
            >
              {selectedLoading && <span className="efn-btn-spinner" aria-hidden="true" />}
              {selectedLoading ? "Generating..." : "Generate Executive Focus Note"}
            </button>
            <button
              type="button"
              className="efn-secondary-btn"
              disabled={isProcessing}
              aria-busy={currentMonthLoading}
              onClick={() => run("current_month")}
            >
              {currentMonthLoading && <span className="efn-btn-spinner" aria-hidden="true" />}
              {currentMonthLoading ? "Preparing view..." : "Current Month Executive View"}
            </button>
          </div>
        </section>

        {isProcessing && <div className="efn-loading">Executive Insight Agent is preparing evidence-backed findings...</div>}
        {error && <div className="efn-error">{error}</div>}

        {data?.data_quality_messages?.length ? (
          <section className="efn-warning">
            {data.data_quality_messages.map((message) => <span key={message}>{message}</span>)}
          </section>
        ) : null}

        {data && (
          <>
            <section className="efn-section">
              <div className="efn-section-header">
                <div>
                  <h2>{data.title || "Executive Focus Note"}</h2>
                  <p>{data.period}</p>
                </div>
              </div>
              <ContextSummary context={data.selected_filter_context} />
              <div className="efn-insight-grid">
                {(data.executive_focus_points || []).map((insight, index) => (
                  <InsightCard key={`${insight.focus_area}-${index}`} insight={insight} onOpenCitation={setActiveCitationId} />
                ))}
              </div>
            </section>

            {SECTION_TABLES.map(([key, title]) => (
              <SortableEvidenceTable
                key={key}
                title={title}
                rows={data?.[key]?.table || []}
                onOpenCitation={setActiveCitationId}
              />
            ))}
          </>
        )}
      </main>

      <CitationDrawer citation={activeCitation} onClose={() => setActiveCitationId(null)} />
      <GuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}

